import Button from 'components/Button';
import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import Select from 'components/form/Inputs/Select';
import Success from 'components/form/Success';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import ActionModal from 'components/Modal/ActionModal';
import { PageBack, PageHeadContainer } from 'components/PageHead';
import DOCUMENTATION_ROLE, {
  documentationRoles,
} from 'enums/DocumentationRole';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import DocumentationFile from 'types/DocumentationFile';
import manageTextError from 'utils/manageTextError';
import privateAPI from 'utils/privateAxiosInstance';

const HeadContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  h1 {
    font-weight: 500;
    font-size: 1.5rem;
    margin-right: 1rem;
  }
`;

const Form = styled.form`
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
  row-gap: 1rem;
  h1 {
    color: black;
    margin-bottom: 0.5rem;
  }
  button {
    margin-left: auto;
    margin-right: 0;
  }
`;

const DeleteButtonContainer = styled.div`
  width: 100%;
  margin-top: 1rem;
  button {
    margin: 0;
  }
`;

const TextContainer = styled.div`
  color: var(--gray-500);
  margin-top: 2rem;
  p {
    margin-bottom: 1rem;
  }
`;

type State = Required<Pick<DocumentationFile, 'title'>> & {
  file: File | null;
};

const initialState: State = {
  title: '',
  file: null,
};

type FieldErrors = {
  [key in 'title' | 'file']: boolean;
};

const initialFieldErrors: FieldErrors = {
  title: false,
  file: false,
};

const Edit: FC = () => {
  // Edit
  const [state, setState] = useState<State>(initialState);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>('');
  const [fieldErrors, setFieldErrors] =
    useState<FieldErrors>(initialFieldErrors);

  const { id } = useParams();
  const { data, error } = useSWR<DocumentationFile>(
    `/files/documentation-file/${id}`,
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSuccess(false);
    setFormError('');
    setFieldErrors(initialFieldErrors);
    return setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    data && setState((prev) => ({ ...prev, title: data.title || '' }));
  }, [data]);

  const handleChangeFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSuccess(false);
    setFormError('');
    setFieldErrors(initialFieldErrors);
    return setState({
      ...state,
      [e.target.name]: e.target.files?.length ? e.target.files[0] : null,
    });
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setFormError('');
    setFieldErrors(initialFieldErrors);

    const formData = new FormData();
    formData.append('title', state.title);
    formData.append('titlee', state.title);
    state.file && formData.append('file', state.file);

    privateAPI
      .put(`/files/documentation-file/${id}`, formData)
      .then(() => {
        setSuccess(true);
      })
      .catch((err) => {
        if (err?.response?.data?.fields && err.response.data.fields[0]) {
          setFieldErrors({
            ...fieldErrors,
            [err.response.data.fields[0]]: true,
          });
        }
        manageTextError(err, setFormError);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Delete
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const handleDelete = () => {
    setDeleteLoading(true);
    privateAPI
      .delete(`/files/documentation-file/${id}`)
      .then(() => {
        mutate('/files/documentation-files');
        navigate('/admin/files');
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  return (
    <>
      <ActionModal
        open={open}
        close={() => setOpen(false)}
        title="Supprimer le fichier"
        apply={handleDelete}
        applyText="Supprimer définitivement"
        color="error"
        loading={deleteLoading}
      >
        <p>
          Vous êtes sur le point de supprimer définitivement le fichier{' '}
          {data?.filename}.
        </p>
        <p>Voulez-vous continuer ?</p>
      </ActionModal>
      <PageHeadContainer>
        <HeadContainer>
          <PageBack href="/admin/files" />
          <h1>Modifier un fichier</h1>
        </HeadContainer>
      </PageHeadContainer>
      {data ? (
        <Form onSubmit={handleSubmit}>
          {formError && <Error>{formError}</Error>}
          {success && <Success>Le fichier a bien été modifié.</Success>}
          <Select label="Type d'utilisateur" value={data?.role || ''} readOnly>
            <option value="">-- Sélectionnez une option --</option>
            {documentationRoles.map((documentationRole, idx) => (
              <option key={idx} value={documentationRole}>
                {DOCUMENTATION_ROLE[documentationRole]}
              </option>
            ))}
          </Select>
          <Input
            name="title"
            label="Titre"
            value={state.title}
            onChange={handleChange}
            required
            error={fieldErrors.title}
          />
          <Input
            name="filename"
            label="Nom du fichier"
            value={data?.filename || ''}
            readOnly
          />
          <Input
            type="file"
            name="file"
            label="Fichier"
            onChange={handleChangeFile}
            error={fieldErrors.file}
            value={state.file?.name}
          />
          <Button color="primary" loading={loading}>
            Modifier le fichier
          </Button>
          <DeleteButtonContainer>
            <Button
              color="error"
              variant="outlined"
              onClick={() => setOpen(true)}
              type="button"
            >
              Supprimer le fichier
            </Button>
          </DeleteButtonContainer>
          <TextContainer>
            <p>
              Le formulaire ci-dessus permet de modifier les fichiers mis à
              disposition des utilisateurs.
            </p>
            <p>
              Le type d'utilisateur et le nom du fichier ne peuvent pas être
              modifiés.
            </p>
            <p>
              Si vous souhaitez modifier l'une de ces informations, vous devez
              supprimer ce fichier à l'aide du bouton ci-dessus, puis recréer un
              nouveau fichier.
            </p>
            <p>Rappel: seuls les fichiers au format PDF sont acceptés.</p>
          </TextContainer>
        </Form>
      ) : error ? (
        <div>Le fichier n'existe pas.</div>
      ) : (
        <LoaderContainer>
          <DefaultLoader size={1.5} />
        </LoaderContainer>
      )}
    </>
  );
};

export default Edit;
